<template>
  <article class="login-container">
    <loading-mask is-full-screen v-bind:is-loading="isLoggingIn" />
  </article>
</template>

<script>
import { RestApiClient } from 'ifield-core-components'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import { useInitializeAtLoggedIn } from '@/composables/UseInitializeAtLoggedIn.js'

/**
 * 転送時にトークンを使ってログインする画面
 */
export default {
  name: 'TheLoginRedirected',
  props: {
    /**
     * 転送時の認証引き継ぎトークン
     */
    redirectToken: {
      type: String,
      default: null,
    },
  },
  inheritAttrs: false,
  setup() {
    const { handleApiError } = useHandleApiError()
    const { onAfterLoginSuccess } = useInitializeAtLoggedIn()
    return { handleApiError, onAfterLoginSuccess }
  },
  data: function () {
    return {
      isLoggingIn: true,
    }
  },
  mounted: function () {
    this.$_onRedirect()
  },
  methods: {
    /**
     * 転送されてきた時の処理
     */
    $_onRedirect: async function () {
      this.$_showLoading()

      try {
        // ログインAPIを実行する
        const login = await this.$_login()
        if (login.status === 'OTP_WAIT') {
          // 多要素認証が未完了
          this.$router.push('/multi-factor-auth')
        } else {
          // storeにログインユーザーの情報を保存する
          this.$store.dispatch('saveLogin', login.data)
          // ログイン後処理
          await this.onAfterLoginSuccess()
          // redirect先が指定されていればそちらのページを起動
          const path = this.$route.query.redirect || '/'
          this.$router.push(path)
        }
      } catch (error) {
        await this.$_showError(error.message)
        this.$router.replace('/login')
      } finally {
        this.$_hideLoading()
      }
    },

    /**
     * ログインAPI実行
     */
    $_login: async function () {
      const url = RestApiClient.createApiUrl('login')
      const option = {
        headers: {
          Authorization: `Bearer ${this.redirectToken}`,
        },
        successStatuses: ['SUCCESS', 'OTP_WAIT'],
      }

      try {
        return await RestApiClient.executePostApi(url, {}, option)
      } catch (error) {
        // OTP_WAIT以外のエラーを処理する
        await this.handleApiError(error, this.$_onLoginFailure, true)
      }
    },

    /**
     * ログイン失敗時処理
     * @param {Response|Error} error APIエラー
     */
    $_onLoginFailure: async function (error) {
      let message

      if (error instanceof Response) {
        const json = await error.json()
        message =
          json.message || this.$t('theLoginRedirected.message.serverError')
      } else {
        message = `${this.$t(
          'theLoginRedirected.message.failedToLogin'
        )}(${error})`
      }

      throw new Error(message)
    },

    /**
     * エラーを表示する
     * @param {string} message エラーメッセージ
     */
    $_showError: function (message) {
      return this.$alert.fire({
        target: this.$el,
        icon: 'error',
        title: this.$t('theLoginRedirected.message.error'),
        text: this.$t('theLoginRedirected.message.failedToTakeOverAuth'),
        input: 'text',
        inputValue: message,
        inputAttributes: {
          disabled: true,
        },
        customClass: {
          input: 'error-detail',
        },
      })
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoggingIn = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoggingIn = false
    },
  },
}
</script>

<style lang="scss" scoped>
.login-container {
  background-color: #fafafa;
  width: 100%;
  height: 100%;

  ::v-deep .error-detail {
    height: $font-size-small * 3;
    font-size: $font-size-small;
    pointer-events: none;
  }
}
</style>
